// Admin "SMS" tab — everyone who gave a phone number, whether they agreed to
// texts and whether they've opted out (routes/adminAnalytics.js /sms-contacts).
// Filter, search, copy a number, or export the list as CSV.
import { useEffect, useMemo, useState } from "react";
import { axiosInstance } from "../../config";
import toast from "react-hot-toast";

const FILTERS = [
  ["canText", "Can text"],
  ["optedOut", "Opted out"],
  ["all", "All"],
];

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : "—");

function toCsv(rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const head = ["email", "username", "phone", "consented", "opted_out", "plan", "joined"];
  const lines = rows.map((c) => [c.email, c.username, c.phone, c.consentAt || "", c.optedOut ? "yes" : "no", c.plan, c.joined].map(esc).join(","));
  return [head.join(","), ...lines].join("\n");
}

export default function SmsTab() {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("canText");
  const [q, setQ] = useState("");

  useEffect(() => {
    axiosInstance.get("/admin/analytics/sms-contacts").then((r) => setData(r.data)).catch((e) => {
      toast.error(e.response?.data?.error || "Failed to load SMS contacts");
      setData({ contacts: [], totals: {} });
    });
  }, []);

  const rows = useMemo(() => {
    const list = (data?.contacts || []).filter((c) =>
      filter === "all" ? true : filter === "optedOut" ? c.optedOut : !c.optedOut);
    const needle = q.trim().toLowerCase();
    return needle
      ? list.filter((c) => [c.email, c.username, c.phone].some((v) => String(v || "").toLowerCase().includes(needle)))
      : list;
  }, [data, filter, q]);

  if (!data) return <div className="admindash-empty admindash-shimmer">Loading…</div>;
  const t = data.totals || {};

  const copy = (phone) => {
    navigator.clipboard?.writeText(phone).then(() => toast.success("Number copied")).catch(() => {});
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(rows)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `clipbait-sms-${filter}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="admindash-section admindash-fade-in">
      <h2 className="admindash-section-title">
        SMS list <span className="muted">({t.all || 0})</span>
      </h2>
      <div className="muted" style={{ fontSize: 13, marginBottom: 16 }}>
        <b>{t.canText || 0}</b> can text · {t.optedOut || 0} opted out · {t.consented || 0} gave consent in the app
      </div>

      <div className="sms-bar">
        <div className="sms-filters">
          {FILTERS.map(([id, label]) => (
            <button key={id} type="button" className={`sms-filter${filter === id ? " on" : ""}`} onClick={() => setFilter(id)}>
              {label}
            </button>
          ))}
        </div>
        <input className="sms-search" placeholder="Search email, name or number" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="sms-export" onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
      </div>

      {rows.length === 0 ? (
        <div className="admindash-empty">No contacts here.</div>
      ) : (
        <div className="funnel-table-scroll">
          <table className="admindash-table">
            <thead>
              <tr>
                <th>Phone</th>
                <th>User</th>
                <th>Plan</th>
                <th>Consent</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id}>
                  <td>
                    <button type="button" className="sms-phone" onClick={() => copy(c.phone)} title="Copy number">{c.phone}</button>
                  </td>
                  <td>
                    <div>{c.email || "—"}</div>
                    {c.username && <div className="muted" style={{ fontSize: 12 }}>@{c.username}</div>}
                  </td>
                  <td>{c.plan === "none" ? "Free" : c.plan}</td>
                  <td>{fmtDate(c.consentAt)}</td>
                  <td>
                    {c.optedOut
                      ? <span className="sms-status out">Opted out {c.optedOutAt ? fmtDate(c.optedOutAt) : ""}</span>
                      : <span className="sms-status ok">Can text</span>}
                  </td>
                  <td>{fmtDate(c.joined)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
